
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";

interface CPVRecommendationsProps {
  selectedCPV: string;
  onCPVSelect: (cpv: string) => void;
  existingSelections: string[];
}

interface CPVCode {
  CPV: number;
  Beskrivelse: string;
}

export const CPVRecommendations = ({
  selectedCPV,
  onCPVSelect,
  existingSelections,
}: CPVRecommendationsProps) => {
  const { data: recommendations, isLoading } = useQuery({
    queryKey: ["cpv-recommendations", selectedCPV],
    queryFn: async () => {
      if (!selectedCPV) return [];

      // Get the selected CPV's first two digits to find related codes
      const selectedPrefix = selectedCPV.substring(0, 2);
      console.log('Selected prefix:', selectedPrefix);
      
      // First, let's check what CPV codes exist with this prefix
      const { data: relatedCPVs, error } = await supabase
        .from("CPV Table")
        .select("CPV, Beskrivelse")
        .gte('CPV', parseInt(selectedPrefix + '000000'))
        .lt('CPV', parseInt(selectedPrefix + '999999'))
        .neq('CPV', parseInt(selectedCPV))
        .order('CPV')
        .limit(10);

      if (error) {
        console.error('Error fetching CPV recommendations:', error);
        throw error;
      }

      console.log('Related CPVs found:', relatedCPVs);

      // Filter out already selected CPVs and return the recommendations
      const filteredRecommendations = (relatedCPVs || [])
        .filter(cpv => !existingSelections.includes(cpv.CPV.toString()))
        .slice(0, 5);

      console.log('Filtered recommendations:', filteredRecommendations);
      return filteredRecommendations;
    },
    enabled: !!selectedCPV,
  });

  if (!selectedCPV || (recommendations?.length === 0 && !isLoading)) {
    console.log('No recommendations to show. Selected CPV:', selectedCPV);
    return null;
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Finding related industries...
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-sm text-muted-foreground">
        Related industries you might be interested in:
      </p>
      <div className="flex flex-wrap gap-2">
        {recommendations?.map((cpv) => (
          <Badge
            key={cpv.CPV}
            variant="secondary"
            className="cursor-pointer hover:bg-secondary/80"
            onClick={() => onCPVSelect(cpv.CPV.toString())}
          >
            {cpv.Beskrivelse}
          </Badge>
        ))}
      </div>
    </div>
  );
};
